<template>
    <Head>
        <title>{{ webshop.name }} - Goedkoopste producten</title>
        <meta head-key="description" name="description"
              :content="`Al vanaf &euro; ${ minPrice.toFixed(2).replace('.', ',') } je rekening vullen. ${ webshop.text }`"
        >
        <meta head-key="keywords" name="keywords"
              :content="`${ webshop.name }, goedkoopste, producten, rekening, mandje, vullen, korting, actiecode, gratis, kado, cadeau, cadeaubon, kortingscode, coupon`">

        <link rel="canonical" :href="webshop.url" />
    </Head>

    <div>
        <div aria-hidden="true" class="relative">
            <img :src="webshop.headerImage" alt="" class="w-full h-72 object-center object-cover"/>
            <div class="absolute inset-0 bg-gradient-to-t from-white"/>
        </div>

        <div class="relative -mt-52 mx-auto pb-16 px-4 md:pb-24 md:px-6 lg:px-8">
            <div class="mx-auto text-center lg:max-w-4xl">
                <h1 class="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">
                    {{ webshop.name }}
                </h1>
                <p class="mt-4 text-sm md:text-base font-semibold">{{ webshop.text }}</p>
            </div>
        </div>
    </div>

    <!-- Filters -->
    <section
        aria-labelledby="filter-heading"
        class="relative z-10 border-t border-b border-gray-200"
    >
        <div class="container grid items-center">
            <h2 id="filter-heading" class="sr-only">Filters</h2>
            <div class="relative col-start-1 row-start-1 py-4">
                <div
                    class="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6 md:divide-x divide-gray-200 text-sm px-4 md:px-6 lg:px-8">
                    <div class="text-gray-700 font-medium flex items-center whitespace-nowrap hover:text-gray-900">
                        Prijs van

                        <currency-input
                            class="w-full md:w-24 h-8 mx-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            v-model="filterForm.price_from"
                            :options="currencyOptions"
                        />
                        tot
                        <currency-input
                            class="w-full md:w-24 h-8 ml-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            v-model="filterForm.price_to"
                            :options="currencyOptions"
                        />
                    </div>

                    <PopoverGroup v-if="categories.length > 1"
                                  class="flex items-center divide-x divide-gray-200 md:pl-6">
                        <Popover class="relative inline-block text-left">
                            <PopoverButton
                                class="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                <span>Categorieën</span>
                                <span v-if="(filterForm.category.length > 0)"
                                      class="ml-1.5 rounded py-0.5 px-1.5 bg-gray-200 text-xs font-semibold text-gray-700 tabular-nums">{{
                                        filterForm.category.length
                                    }}</span>
                                <ChevronDownIcon
                                    class="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                    aria-hidden="true"/>
                            </PopoverButton>

                            <transition enter-active-class="transition ease-out duration-100"
                                        enter-from-class="transform opacity-0 scale-95"
                                        enter-to-class="transform opacity-100 scale-100"
                                        leave-active-class="transition ease-in duration-75"
                                        leave-from-class="transform opacity-100 scale-100"
                                        leave-to-class="transform opacity-0 scale-95">
                                <PopoverPanel
                                    class="origin-top-right absolute mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <form class="space-y-4">
                                        <div v-for="category in categories" :key="category" class="flex items-center">
                                            <input v-model="filterForm.category" :id="`filter-${category}`"
                                                   name="category[]" :value="category" type="checkbox"
                                                   class="h-4 w-4 border-gray-300 rounded text-indigo-600 focus:ring-indigo-500"/>
                                            <label :for="`filter-${category}`"
                                                   class="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                {{ category }}
                                            </label>
                                        </div>
                                    </form>
                                </PopoverPanel>
                            </transition>
                        </Popover>
                    </PopoverGroup>

                    <div class="pl-6 hidden md:flex items-center">
                        <Link
                            v-if="filtered"
                            :href="webshop.url"
                            :only="['products']"
                            class="text-gray-500"
                            preserve-scroll
                        >
                            Toon alles
                        </Link>
                    </div>
                </div>
            </div>

            <div class="flex justify-between md:justify-end col-start-1 row-start-2 md:row-start-1 py-4">
                <div class="flex md:hidden px-4 md:px-6 lg:px-8">
                    <Link
                        v-if="filtered"
                        :href="webshop.url"
                        :only="['products']"
                        class="text-sm font-medium text-gray-700"
                        preserve-scroll
                    >
                        Toon alles
                    </Link>
                </div>

                <div class="flex justify-end px-4 md:px-6 lg:px-8">
                    <Menu as="div" class="relative inline-block">
                        <div class="flex">
                            <MenuButton
                                class="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                Sorteer
                                <ChevronDownIcon
                                    class="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                    aria-hidden="true"/>
                            </MenuButton>
                        </div>

                        <transition enter-active-class="transition ease-out duration-100"
                                    enter-from-class="transform opacity-0 scale-95"
                                    enter-to-class="transform opacity-100 scale-100"
                                    leave-active-class="transition ease-in duration-75"
                                    leave-from-class="transform opacity-100 scale-100"
                                    leave-to-class="transform opacity-0 scale-95">
                            <MenuItems
                                class="origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div class="py-1">
                                    <MenuItem v-slot="{ active }">
                                        <div
                                            @click="filterForm.sort = 'asc'"
                                            :class="[filterForm.sort === 'asc' ? 'font-medium text-gray-900' : 'text-gray-500', active ? 'bg-gray-100' : '', 'px-4 py-2 text-sm cursor-pointer']"
                                        >
                                            Prijs: laag naar hoog
                                        </div>
                                    </MenuItem>
                                    <MenuItem v-slot="{ active }">
                                        <div
                                            @click="filterForm.sort = 'desc'"
                                            :class="[filterForm.sort === 'desc' ? 'font-medium text-gray-900' : 'text-gray-500', active ? 'bg-gray-100' : '', 'px-4 py-2 text-sm cursor-pointer']"
                                        >
                                            Prijs: hoog naar laag
                                        </div>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </div>
        </div>
    </section>

    <!-- Product grid -->
    <section aria-labelledby="products-heading" class="container">
        <h2 id="products-heading" class="sr-only">Products</h2>

        <div
            class="-mx-px border-l border-gray-200 grid grid-cols-2 md:mx-0 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <div v-for="product in products.data" :key="product.id"
                 class="group relative p-4 border-r border-b border-gray-200 md:p-6">
                <div
                    class="rounded-lg overflow-hidden bg-gray-200 aspect-w-1 aspect-h-1 group-hover:opacity-75">
                    <img :src="product.image" :alt="product.name"
                         class="w-full h-full object-center object-cover"/>
                </div>
                <div class="pt-10 pb-4 text-center">
                    <h3 class="text-sm font-medium text-gray-900">
                        <a :href="product.url" target="_blank" rel="nofollow">
                            <span aria-hidden="true" class="absolute inset-0"/>
                            {{ product.name }}
                        </a>
                    </h3>
                    <p class="mt-4 text-base font-medium text-gray-900">{{ product.price }}</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Pagination -->
    <pagination class="mt-6" :links="products.links"/>
</template>

<script>
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
} from '@headlessui/vue';
import {ChevronDownIcon} from '@heroicons/vue/solid';
import {ref} from "vue";
import throttle from 'lodash/throttle';
import {Head, Link} from '@inertiajs/inertia-vue3';
import Pagination from '../../Components/Pagination';
import CurrencyInput from '../../Components/CurrencyInput';

export default {
    components: {
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
        TransitionChild,
        TransitionRoot,
        Popover,
        PopoverButton,
        PopoverGroup,
        PopoverPanel,
        ChevronDownIcon,
        Pagination,
        Head,
        Link,
        CurrencyInput,
    },

    props: {
        webshop: {
            type: Object,
            required: true
        },
        filter: {
            type: Object,
            required: false,
            default: () => {},
        },
        minPrice: {
            type: Number,
            required: true
        },
        maxPrice: {
            type: Number,
            required: true
        },
        categories: {
            type: Array,
            required: true
        },
        products: {
            type: Object,
            required: true
        },
    },

    setup(props) {
        const currencyOptions = {
            locale: undefined,
            currency: 'EUR',
            currencyDisplay: 'symbol',
            valueRange: {
                min: props.minPrice,
                max: props.maxPrice
            },
            precision: undefined,
            hideCurrencySymbolOnFocus: false,
            hideGroupingSeparatorOnFocus: false,
            hideNegligibleDecimalDigitsOnFocus: false,
            autoDecimalDigits: false,
            valueScaling: undefined,
            autoSign: true,
            useGrouping: true
        };

        const filterForm = ref({
            price_from: props.filter.price_from ? Number(props.filter.price_from) : props.minPrice,
            price_to: props.filter.price_to ? Number(props.filter.price_to) : props.maxPrice,
            category: props.filter.category ? props.filter.category : [],
            sort: props.filter.sort ? props.filter.sort : 'asc',
        });

        return {
            filterForm,
            currencyOptions,
        }
    },

    watch: {
        filterForm: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.webshop.url, this.filterForm, {
                    preserveState: true,
                    preserveScroll: true,
                    only: ['products']
                })
            }, 1500),
        },
    },

    computed: {
        filtered: function () {
            return this.filterForm.price_from !== this.minPrice ||
                this.filterForm.price_to !== this.maxPrice ||
                this.filterForm.category.length > 0;
        },
    },
}
</script>
